import { BaseQueryFn, createApi, FetchArgs } from "@reduxjs/toolkit/query/react";

import { fetchMiniMapData } from "features/miniMap/fetchMiniMapData";
import type { MiniMapPayload } from "features/miniMap/minimapSlice";
import { Viewpoint } from "features/viewpoints/types";

import { procoreClient } from "./client";
import { Model, ModelRevision } from "./types";

export type ProcoreBaseQueryError = { status: number; statusText?: string; data?: unknown };

/**
 * Convert a thrown error into a plain, serializable query error.
 *
 * RTK Query stores errors in redux state, and redux's serializability check warns
 * (on every subsequent dispatch) if anything non-serializable ends up there. The
 * Procore client throws `ResponseError` instances carrying raw `Response`/`Request`
 * objects, so only the parsed response body and message are kept.
 */
function toSerializableQueryError(err: unknown, fallbackStatusText: string): ProcoreBaseQueryError {
    const e = err as { status?: number; statusText?: string; data?: unknown; message?: string };
    return {
        status: e?.status ?? 500,
        statusText: e?.statusText ?? e?.message ?? fallbackStatusText,
        data: e?.data ?? e?.message,
    };
}

/** Ordered query-param variants tried by `getModelRevisions`; first non-empty result wins. */
function getBimModelRevisionParamCandidates(params: {
    sceneId: string;
    sceneVersionId?: string;
}): Record<string, string>[] {
    const { sceneId, sceneVersionId } = params;

    const candidates: Record<string, string>[] = [];

    if (sceneVersionId) {
        if (sceneId !== sceneVersionId) {
            candidates.push({ external_scene_version_id: sceneVersionId, external_scene_id: sceneId });
        }
        candidates.push({ external_scene_version_id: sceneVersionId });
    } else if (sceneId) {
        candidates.push({ external_scene_version_id: sceneId });
    }

    if (sceneId) {
        candidates.push({ external_scene_id: sceneId });
        candidates.push({ "filters[current_platform_scene_id]": sceneId });
    }

    return candidates;
}

/**
 * RTK Query base query that uses the shared Procore HTTP client.
 * Supports string URL (GET) or FetchArgs (url, method, body, params).
 */
const procoreBaseQuery: BaseQueryFn<string | FetchArgs, unknown, ProcoreBaseQueryError> = async (
    args,
    _api,
    _extraOptions,
) => {
    const url = typeof args === "string" ? args : args.url;
    const method = (typeof args === "string" ? "GET" : args.method) || "GET";
    const body = typeof args === "object" && "body" in args ? args.body : undefined;
    const params = typeof args === "object" && "params" in args ? args.params : undefined;

    try {
        const data = await procoreClient<unknown>(url, { method, body, params });
        return { data };
    } catch (err: unknown) {
        return { error: toSerializableQueryError(err, "Unknown error") };
    }
};

export type GetViewpointResp = {
    data: {
        id: string;
        name: string;
        payload: Viewpoint;
        scene_id: string;
        snapshot_url: string | null;
    };
};

/**
 * @see https://github.com/procore/bim-model-manager-service/blob/main/docs/open-api.json
 */
export const procoreApi = createApi({
    reducerPath: "procoreApi",
    baseQuery: procoreBaseQuery,
    tagTypes: ["Viewpoint", "MiniMap"],
    endpoints: (builder) => ({
        getViewpoint: builder.query<GetViewpointResp, { companyId: number; projectId: number; viewpointId: string }>({
            query: ({ companyId, projectId, viewpointId }) =>
                `/rest/v2.0/companies/${companyId}/projects/${projectId}/bim/viewpoints/${viewpointId}`,
            providesTags: (_result, _err, { companyId, projectId, viewpointId }) => [
                { type: "Viewpoint", id: `${companyId}-${projectId}-${viewpointId}` },
            ],
        }),
        getModelRevisions: builder.query<
            ModelRevision[],
            { companyId: number; projectId: number; sceneId: string; sceneVersionId: string; latest?: boolean }
        >({
            queryFn: async (
                { companyId, projectId, sceneId, sceneVersionId, latest },
                _api,
                _extraOptions,
                baseQuery,
            ) => {
                const basePath = `/rest/v2.0/companies/${companyId}/projects/${projectId}/bim/model_revisions`;

                const candidates = getBimModelRevisionParamCandidates({ sceneId, sceneVersionId });

                let lastRespError: ProcoreBaseQueryError | undefined;
                for (const _query of candidates) {
                    const query = { ..._query, latest };
                    try {
                        const response = await baseQuery({
                            url: basePath,
                            method: "GET",
                            params: query,
                        });
                        lastRespError = response.error;
                        const respData = response.data as { data: ModelRevision[] };
                        if (respData && respData.data.length > 0) {
                            return { data: respData.data };
                        }
                    } catch {
                        console.error("Failed to get BIM model revisions with query:", query);
                    }
                }

                return lastRespError ? { error: lastRespError } : { data: [] };
            },
        }),
        getModels: builder.query<{ data: Model[] }, { companyId: number; projectId: number }>({
            query: ({ companyId, projectId }) => ({
                url: `/rest/v2.0/companies/${companyId}/projects/${projectId}/bim/models`,
            }),
        }),
        /**
         * Fetch mini map data (plans -> locations -> drawings) for a known
         * model revision.
         *
         * The cache is keyed by `{ companyId, projectId, modelRevisionId }`.
         * The caller (`useMiniMapData`) resolves `modelRevisionId` first via
         * `useGetModelRevisionsQuery({ ...args, latest: true })` and passes
         * it here. The mini map slice mirrors the fulfilled payload into slice
         * state via `extraReducers` so existing selectors keep working.
         *
         * Refresh from the host app via the public SDK:
         *
         *     webviewer.gui.forceMiniMapRefresh();
         *
         * which dispatches `procoreApi.util.invalidateTags(["MiniMap"])`.
         */
        getMiniMapData: builder.query<
            MiniMapPayload,
            { companyId: number; projectId: number; modelRevisionId: string }
        >({
            providesTags: ["MiniMap"],
            async queryFn({ companyId, projectId, modelRevisionId }) {
                try {
                    const data = await fetchMiniMapData({ companyId, projectId, modelRevisionId });
                    return { data };
                } catch (err: unknown) {
                    return { error: toSerializableQueryError(err, "Failed to fetch mini map data") };
                }
            },
        }),
    }),
});

export const {
    useGetViewpointQuery,
    useLazyGetViewpointQuery,
    useGetModelRevisionsQuery,
    useLazyGetModelRevisionsQuery,
    useGetModelsQuery,
    useLazyGetModelsQuery,
    useGetMiniMapDataQuery,
    useLazyGetMiniMapDataQuery,
} = procoreApi;
